/** SPIN in-app expert modes (staff-facing). Not Cursor subagents — shared config for landing tiles + API prompt routing. */

export type SpinExpertModeId = 'general' | 'revenue' | 'finance' | 'operations';

export type SpinExpertStarter = {
  title: string;
  description: string;
  prompt: string;
};

export type SpinExpertMode = {
  id: SpinExpertModeId;
  /** Short label for landing tiles (e.g. "Revenue", "Finance") */
  expertiseLabel: string;
  /** Appended to SPIN system prompt when this mode is active */
  systemPromptAppend: string;
  starters: SpinExpertStarter[];
  /** Lower appears first when flattening landing starters */
  landingOrder: number;
};

export const SPIN_EXPERT_MODES: SpinExpertMode[] = [
  {
    id: 'revenue',
    expertiseLabel: 'Revenue',
    landingOrder: 1,
    systemPromptAppend: [
      'Expert mode — Revenue management: act as a senior golf revenue advisor grounded in Tebox. Maximize total revenue, utilization, and long-term yield (not only green fees).',
      'Call get_revenue_utilization_summary for utilization, daypart breakdown, and booking pace; get_insights_feed for prescriptive cards; list_club_season_goals for targets; get_reporting_eod_summary when dollar context is needed.',
      'Call get_product_help with "revenue management" for Tebox pricing layers (base schema, dynamic pricing, coupons) and the pricing-lock rule.',
      'Never recommend retroactive price changes on existing reservations. Do not discount simply because inventory exists — prefer targeted coupons or dynamic rules for specific dayparts.',
      'Say which Tebox layer to change: base pricing schema, dynamic pricing policy, or coupon. SPIN does not apply price changes automatically.',
      'If data looks healthy, say no pricing changes recommended at this time.',
    ].join(' '),
    starters: [
      {
        title: 'Revenue & utilization',
        description: 'Utilization by daypart, booking pace vs last period, and pricing guidance.',
        prompt:
          'How is our tee sheet utilization and booking pace over the last two weeks? Summarize by daypart and note anything worth a pricing or promotion change.',
      },
    ],
  },
  {
    id: 'finance',
    expertiseLabel: 'Finance',
    landingOrder: 2,
    systemPromptAppend: [
      'Expert mode — Finance & reporting: help staff interpret Tebox reporting and payment data for this club. This is operational finance (EOD totals, payment mix, wallet activity) — not statutory accounting review or journal posting design.',
      'Call get_reporting_eod_summary for revenue by category, payment mix, tax, and wallet summary (start_date and end_date required). Use list_transactions for individual payment lines.',
      'Call get_product_help for "reporting", "wallet", "ledger", or "data model" when explaining how numbers are calculated, how entities connect, or what reports to open. For conceptual "how does money flow" or "how does X relate to Y" questions, prefer get_product_help query "data model" or "finance data model" and suggest the interactive map at /reporting/data-model (Customers: /customers/data-model, Products: /inventory/data-model).',
      'Distinguish reservation/play revenue from membership and pass purchase revenue — they are different facts; do not double-count.',
      'After summaries, include markdown links from tool JSON (links.end_of_day_report, links.reporting_dashboard) when present.',
      'Do not invent ledger balances or revenue recognition entries; for complex accounting policy, advise staff to confirm with their controller.',
    ].join(' '),
    starters: [
      {
        title: 'EOD & payments',
        description: 'Revenue totals, payment mix, and taxes for a recent date range.',
        prompt:
          'Summarize our end-of-day revenue for the last 7 days: categories, payment mix, and anything notable vs the prior week.',
      },
      {
        title: 'How money moves',
        description: 'Explain Tebox transactions, liabilities, wallet, and gift cards.',
        prompt:
          'Explain how money flows in Tebox — transactions, revenue vs liability, wallet charges vs deposits, and gift cards. When should I use the finance data model map?',
      },
    ],
  },
  {
    id: 'operations',
    expertiseLabel: 'Operations',
    landingOrder: 3,
    systemPromptAppend: [
      'Expert mode — Day-to-day operations: prioritize live club data and clear next steps for tee sheet and CRM work.',
      'Use get_insights_feed for operational signals; list_reservations and list_tee_times_for_date for tee sheet; lookup_customers and get_customer_profile for CRM.',
      'Be concise and action-oriented. Link to in-app paths from tool results when helpful.',
    ].join(' '),
    starters: [
      {
        title: 'Insights feed',
        description: 'Highlights and signals from your club insights for today.',
        prompt: "What's on the insights feed today?",
      },
      {
        title: 'Tomorrow’s tee sheet',
        description: 'Reservations and tee times for the next day.',
        prompt: 'List reservations for tomorrow',
      },
      {
        title: 'Customer lookup',
        description: 'Find a golfer’s profile, history, and contact details.',
        prompt: 'Look up a customer by name',
      },
    ],
  },
  {
    id: 'general',
    expertiseLabel: 'Help',
    landingOrder: 4,
    systemPromptAppend: '',
    starters: [
      {
        title: 'Hours & policies',
        description: 'Quick answers on operations, rules, and how you run the club.',
        prompt: 'What are our opening hours and policies?',
      },
    ],
  },
];

const MODE_BY_ID = new Map(SPIN_EXPERT_MODES.map((m) => [m.id, m]));

export function isSpinExpertModeId(value: string | null | undefined): value is SpinExpertModeId {
  return value != null && MODE_BY_ID.has(value as SpinExpertModeId);
}

export function getSpinExpertMode(
  id: SpinExpertModeId | string | null | undefined
): SpinExpertMode | undefined {
  if (!id || !isSpinExpertModeId(id)) return undefined;
  return MODE_BY_ID.get(id);
}

export function getSpinExpertModePromptAppend(
  id: SpinExpertModeId | string | null | undefined
): string {
  const mode = getSpinExpertMode(id);
  if (!mode?.systemPromptAppend.trim()) return '';
  return mode.systemPromptAppend.trim();
}

export type SpinLandingStarter = SpinExpertStarter & {
  expertModeId: SpinExpertModeId;
  expertiseLabel: string;
};

/** Flattened starters for the SPIN empty-state landing grid (stable order). */
export function getSpinLandingStarters(): SpinLandingStarter[] {
  return [...SPIN_EXPERT_MODES]
    .sort((a, b) => a.landingOrder - b.landingOrder)
    .flatMap((mode) =>
      mode.starters.map((starter) => ({
        ...starter,
        expertModeId: mode.id,
        expertiseLabel: mode.expertiseLabel,
      }))
    );
}
